import { AbstractComponentFlow } from './AbstractComponentFlow';
import { IProcessManifestProps } from '../lifecycle';
import { ManifestStore, ManifestComponentExtensionType } from '../stores';
import { EditorElementBuilder } from '@wix/component-protocol/builder';
import {
  ComponentInitialSize,
  DashboardAction,
  LAYOUT,
  ACTIONS,
} from '@wix/component-protocol/schema';
import {
  CustomElementWidget,
  HeightMode,
  InstallationInfo,
  StaticContainer,
  PanelSizeWidth,
  WidgetSize,
} from '@wix/ambassador-devcenter-components-v1-component/types';

export class CustomElementComponentFlow extends AbstractComponentFlow<ManifestStore> {
  static extensionType = ManifestComponentExtensionType.CUSTOM_ELEMENT as const;
  public readonly type = CustomElementComponentFlow.extensionType;

  // we don't have panels to load for CE components
  async loadPanels() {}

  async loadManifest({
    appDataComponent,
    isPendingApplication,
  }: IProcessManifestProps) {
    if (!appDataComponent.data || !appDataComponent.componentId) {
      return;
    }

    const widgetData = appDataComponent.data as CustomElementWidget;

    const builder = new EditorElementBuilder();
    builder.setDisplayName(widgetData.base?.name ?? appDataComponent.name);
    builder.setLayout({
      resizeDirection: LAYOUT.RESIZE_DIRECTION.horizontalAndVertical,
      contentResizeDirection:
        LAYOUT.CONTENT_RESIZE_DIRECTION.horizontalAndVertical,
      disableStretching: !widgetData.size?.width?.allowStretch,
      disablePositioning: false,
      disableRotation: true,
    });

    builder.setAction('settings', {
      displayName: 'Settings',
      execution: {
        actionType: ACTIONS.ACTION_TYPE.panel,
        panel: {
          panelType: ACTIONS.PANEL_TYPE.inline,
          inline: {
            contentType: ACTIONS.PANEL_CONTENT_TYPE.url,
            url: widgetData.behaviors?.settings?.settingsUrl ?? '',
            size: {
              width: PanelSizeWidth.SMALL,
              height: 517,
            },
          },
        },
      },
    });

    if (widgetData.behaviors?.dashboard?.dashboardPageComponentId) {
      builder.setAction('dashboard', {
        dashboardPageId:
          widgetData.behaviors.dashboard.dashboardPageComponentId,
      } as DashboardAction);
    }

    const sizeMapper = (size: WidgetSize): ComponentInitialSize => {
      const isStretched = size.width?.stretchByDefault;
      const isHeightFixed = size.height?.heightMode === HeightMode.FIXED;
      return {
        width: {
          sizingType: isStretched
            ? LAYOUT.SIZING_TYPE.stretched
            : LAYOUT.SIZING_TYPE.pixels,
          ...(isStretched ? {} : { pixels: size.width?.defaultWidth }),
        },
        height: {
          sizingType: isHeightFixed
            ? LAYOUT.SIZING_TYPE.pixels
            : LAYOUT.SIZING_TYPE.content,
          // TODO: According to product definition: size has minimum value and in case content is bigger than the minimum value, the component will adjust to fit the content.
          ...(isHeightFixed ? { pixels: size.height?.defaultHeight } : {}),
        },
      };
    };

    const preset = widgetData.presets?.[0];
    if (preset && preset.id) {
      const presetSize = preset.breakpoints?.large?.size;
      builder.setPresets({
        [preset.id]: {
          displayName: preset.name,
          thumbnailUrl: preset.thumbnailUrl,
          ...(presetSize && { initialSize: sizeMapper(presetSize) }),
        },
      });
    }

    const installation: InstallationInfo = {
      staticContainer:
        widgetData.installation?.staticContainer ??
        (widgetData.installation?.base?.autoAdd
          ? StaticContainer.HOMEPAGE
          : StaticContainer.UNKNOWN_CONTAINER),
      ...(widgetData.size && { initialSize: sizeMapper(widgetData.size) }),
    };

    const manifest = {
      editorElement: builder.build(),
      description: widgetData.base?.description,
      installation,
    };

    this.manifestStore.setManifest(
      appDataComponent.componentId,
      ManifestComponentExtensionType.CUSTOM_ELEMENT,
      manifest,
      null,
      isPendingApplication,
    );
  }
}
